/**
 * EditorConfig — single visible toggle surface for the /editor URL.
 *
 * The /editor mode is DJ's one-click way back into the in-game level
 * editor: it boots the `editor` DevConfig preset (which flips
 * `DevConfig.systems.editor = true` and loads the empty-canvas map) and
 * opens the EditorOverlay on arrival (`editorOn` → `?editorOn=1` →
 * `DevConfig.editorOverlayOnByDefault`). Together those two satisfy the
 * `DevConfig.systems.editor && isEditorMode` gate in GameUIOverlay, so
 * the EditorOverlay mounts straight away — no Backquote toggle, no
 * DevTools "Enable Editor" click.
 *
 * Mirrors BrawlConfig / ArenaConfig wiring:
 *   - main.tsx → reads it on /editor mount via coerceModeUrl; injects URL
 *     params (map + preset + editorOn + skip screens) so the rest of the
 *     app sees a fully-formed boot URL without DJ typing anything.
 *
 * Edit values here — no need to touch main.tsx, coerceModeUrl, or the
 * editor systems. The map below is the canonical blank editing surface;
 * point `mapId` at any GridLevel/PlatformerLevel id to start from a
 * different base layout.
 */

export const EDITOR_CONFIG = {
  // Reset on load (like /brawl /arena) — clears the session so the editor boots
  // CLEAN onto empty-canvas instead of the persisted character's last server
  // map (e.g. DJ Live Lobby) with no editor overlay. The reset is now SAFE for
  // saved maps: `applyClientResetCheck` preserves `helix_level_*` +
  // `helix_levels_index` (user-authored maps survive the wipe); only session /
  // dev-scratch keys are cleared. See clientResetVersion.ts PRESERVE_PREFIXES.
  resetOnLoad: true,

  // Map the editor opens on. empty-canvas is the blank slate; the
  // `editor` preset already defaults to it (PRESET_DEFAULT_MAPS), but we
  // inject it explicitly so the URL is self-describing + a future preset
  // default change can't silently move the editor off the canvas.
  mapId: 'empty-canvas' as const,

  // Boots the `editor` DevConfig preset (systems.editor = true + the
  // single-player-plus-edit QA stack: combat/chat/hud/debugToolbox on).
  preset: 'editor' as const,

  // Open the EditorOverlay on arrival (bypasses the Backquote toggle).
  // Maps to ?editorOn=1 → DevConfig.editorOverlayOnByDefault → the
  // useEditorMode hook seeds isEditorMode = true on first render.
  editorOn: true,

  // Auto-skip the splash + login click-through screens.
  skipSplash: true,
  skipLogin: true,

  // Auto-pick a Mage — the editor doesn't care which class you are, so clicking
  // "Editor" should drop you straight into the tools. Keep the PreGameFlow
  // resolver enabled so Mongo-backed PTR/tunnel rooms receive a real roster id;
  // strict localhost still takes useAuth's synthetic fast path.
  autoClass: 'mage',
  autoClassPicker: true,
} as const;
